import { getDisplayColumnPrefix, isAnyPeriodColumn } from './periodUtils';

const hasOwn = (value, key) => (
  value !== null
  && typeof value === 'object'
  && Object.prototype.hasOwnProperty.call(value, key)
);

// A table column whose cells are objects in some rows and plain values in others
// (a legal Function/API result — the manifest types it `mixed`) is drawn as a
// group of the object keys, which leaves the plain values with no column. The
// header builders add this reserved leaf under such a group; it reads the
// row's bare value and is empty where the row holds an object. The id is a
// sentinel, not the label, so a real key named "Single value" still works.
export const SINGLE_VALUE_LEAF = '__single_value__';
export const SINGLE_VALUE_LABEL = 'Single value';

const isPlainObject = (value) => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

// The cell read, with the leaf path already split. A caller reading ONE column across
// many rows splits the id once and calls this per row, rather than re-splitting the
// same id for every cell it touches.
export function getRawCellValueByPath(row, pathParts) {
  let value = row;
  for (const part of pathParts) {
    if (part === SINGLE_VALUE_LEAF) return isPlainObject(value) ? null : value;
    if (!hasOwn(value, part)) return null;
    value = value[part];
  }
  return value;
}

export function getRawCellValue(row, columnId, delimiter = '§') {
  return getRawCellValueByPath(row, String(columnId).split(delimiter));
}

function addMetricTypes(typeMap, schema, prefix = '') {
  Object.entries(schema || {}).forEach(([name, config]) => {
    if (!config || typeof config !== 'object') return;

    const path = prefix ? `${prefix}.${name}` : name;
    if (Object.prototype.hasOwnProperty.call(config, 'type')) {
      typeMap.set(path, config.type);
      return;
    }

    const nestedSchema = (
      config.schema
      && typeof config.schema === 'object'
      && !Array.isArray(config.schema)
    ) ? config.schema : config;
    addMetricTypes(typeMap, nestedSchema, path);
  });
}

export function buildTableColumnTypeMap(tableSchema) {
  const typeMap = new Map();
  if (!tableSchema) return typeMap;

  const scalarColumns = [
    tableSchema.identifier,
    ...(tableSchema.detailColumns || []),
    ...(tableSchema.attributeColumns || []),
    ...(tableSchema.tableAttributeColumns || []),
  ];
  scalarColumns.forEach((column) => {
    if (column?.name) typeMap.set(column.name, column.type || null);
  });

  addMetricTypes(
    typeMap,
    tableSchema.metricColumns?.schema || {},
    getDisplayColumnPrefix(tableSchema),
  );
  return typeMap;
}

export function getTableLeafType(tableSchema, typeMap, columnId, delimiter = '§') {
  const parts = String(columnId).split(delimiter);

  if (tableSchema?.tableType === 'timeSeries' && isAnyPeriodColumn(parts[0])) {
    const metricPath = parts.slice(1).join('.');
    if (!metricPath) return null;
    return typeMap.get(`${getDisplayColumnPrefix(tableSchema)}.${metricPath}`) || null;
  }

  return typeMap.get(parts.join('.')) || null;
}

export const isDateType = (declaredType) => (
  String(declaredType || '').trim().toLowerCase() === 'date'
);

// Parse an actual calendar day in UTC, without rolling an invalid day forward.
function parseIsoDay(value) {
  if (typeof value !== 'string' || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (year < 1) return null;
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  return date;
}

// 'DD MMM YY' for an ISO day value in a DECLARED-Date leaf. Callers gate on the
// declaration (isDateType / a date-leaf set); this function only makes the text.
// A value that is not exactly YYYY-MM-DD, or names an impossible calendar day
// (2026-02-31), is returned unchanged — never rolled into a nearby real date
// and never reinterpreted by shape (M66).
export function formatDeclaredDate(value) {
  const date = parseIsoDay(value);
  if (!date) return value;
  // A date-only value names a calendar day, not an instant: format it in UTC so a
  // browser west of UTC does not roll midnight-UTC back to the previous local day.
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'UTC' });
}

export function toExcelCellValue(rawValue, declaredType = null) {
  if (rawValue === null || rawValue === undefined || rawValue === '') return '';

  if (typeof rawValue === 'object') return JSON.stringify(rawValue);

  const normalizedType = String(declaredType || '').trim().toLowerCase();
  if (normalizedType === 'text' || normalizedType === 'string') {
    return String(rawValue);
  }

  if (normalizedType === 'date') {
    const date = parseIsoDay(rawValue);
    // Excel's standard date system starts in 1900. Earlier calendar dates
    // remain text so the workbook does not display an unsupported serial.
    if (date && date.getUTCFullYear() >= 1900) return date;
  }
  if (['number', 'float', 'float64', 'integer', 'int', 'numeric'].includes(normalizedType)
      && typeof rawValue === 'string'
      && /^[+-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:[eE][+-]?[0-9]+)?$/.test(rawValue.trim())) {
    const number = Number(rawValue);
    if (Number.isFinite(number)) return number;
  }
  if (['yes/no', 'boolean', 'bool'].includes(normalizedType)) {
    if (rawValue === 1) return true;
    if (rawValue === 0) return false;
    if (typeof rawValue === 'string') {
      const value = rawValue.trim().toLowerCase();
      if (['true', 'yes', 'y', '1'].includes(value)) return true;
      if (['false', 'no', 'n', '0'].includes(value)) return false;
    }
  }

  // Without a usable declaration, preserve the JSON value's own type. Text
  // that cannot be converted remains visible as written, never guessed or nulled.
  return rawValue;
}

// ── ad-hoc workbook layout ───────────────────────────────────────────────────
// A worksheet written straight from an array opens at Excel's default column
// width, which clips ordinary identifiers, names and statuses until the reader
// resizes every column by hand. Each column is sized to its longest value
// (header included), within bounds: never narrower than a short code needs,
// never so wide that one long cell — a JSON blob, a paragraph — pushes the
// rest of the sheet off screen. SheetJS's community build writes widths but
// not cell styles, so a value past the ceiling still clips; the ceiling is
// generous enough that ordinary text does not reach it.
const MIN_COLUMN_CHARS = 8;
const MAX_COLUMN_CHARS = 60;
const COLUMN_PADDING_CHARS = 2;

const cellChars = (value) => {
  if (value === null || value === undefined) return 0;
  const text = typeof value === 'string'
    ? value
    : typeof value === 'number'
      ? value.toLocaleString('en-GB')
      : value instanceof Date
        ? 'dd/mm/yyyy'
        : String(value);
  return text.split('\n').reduce((longest, line) => Math.max(longest, line.length), 0);
};

// `rows` is the array-of-arrays the worksheet was built from. Returns the
// `!cols` list: one `{ wch }` per column.
export function fitColumnWidths(rows, { min = MIN_COLUMN_CHARS, max = MAX_COLUMN_CHARS } = {}) {
  const widths = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    (Array.isArray(row) ? row : []).forEach((value, index) => {
      widths[index] = Math.max(widths[index] || 0, cellChars(value));
    });
  });
  return widths.map((chars) => ({ wch: Math.min(max, Math.max(min, chars + COLUMN_PADDING_CHARS)) }));
}

export function applyColumnWidths(worksheet, rows, options) {
  worksheet['!cols'] = fitColumnWidths(rows, options);
  return worksheet;
}

// The name an ad-hoc workbook is saved under: the parts (function or view
// name, output label, run stamp) as file-safe words joined by underscores —
// "loss-history_totals_20260829-1412" — so a download says where it came from.
// Falls back to the old generic name only when no part is known.
export const exportFileName = (...parts) => {
  const words = parts
    .filter((part) => part !== null && part !== undefined)
    .map((part) => String(part).trim().replace(/[^A-Za-z0-9-]+/g, '-').replace(/^-+|-+$/g, ''))
    .filter(Boolean);
  return words.length > 0 ? words.join('_') : 'table-data';
};
